/** @jsxImportSource @emotion/react */
import { useEffect, useMemo, useState } from 'react';
import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'
import { css } from '@emotion/react'
import { Document, Page, pdfjs } from 'react-pdf'

import NewPatient from './NewPatient';
import { IncomingDocumentType } from '../types';
import NewPatientOrder from './NewPatientOrder';
import { useAppDispatch, useAppSelector } from '../../../../hooks/redux';
import { newPatientInHistorySelector, patientHistoryReset } from '../../../../store/slice/patientSlice';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/legacy/build/pdf.worker.min.js`

interface IProps {
  show?: string
  onClose: () => void
  currentFax: IncomingDocumentType
}

const CreateOrderDialog = ({ show, onClose, currentFax }: IProps) => {
  const [numPages, setNumPages] = useState<number | null>(null)
  const [rotation, setRotation] = useState<number>(0)
  const dispatch = useAppDispatch();

  const { officeId, ...newPatient } = useAppSelector(newPatientInHistorySelector) || {}
  const docUrl = useMemo(() => currentFax.mediaUrl, [show])

  const rotateLeft = () => setRotation((prev) => (prev - 90 + 360) % 360)
  const rotateRight = () => setRotation((prev) => (prev + 90) % 360)

  useEffect(() => {
    if (newPatient?.firstName) {
      dispatch(patientHistoryReset());
    }
    setRotation(0)
  }, [show])

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages)
  }

  return (
    <Modal centered show={!!show} onHide={onClose} size="xl">
      <Modal.Header>
        <Modal.Title>Create Order From Incoming Document</Modal.Title>
      </Modal.Header>
      <Modal.Body
        css={css`
          display: flex;
        `}>
        <div
          css={css`
            flex: 1;
            padding-right: 10px;
            border-right: 1px solid lightgray;
          `}>
          <div className="d-flex">
            {newPatient?.firstName ? (
              <NewPatientOrder currentFax={currentFax} rotation={rotation} />
            ) : (
              <NewPatient patientData={currentFax?.patientData} />
            )}
          </div>
        </div>
        <div
          css={css`
            max-height: 80vh;
            overflow-y: auto;
          `}>
          <div
            css={css`
              display: flex;
              justify-content: center;
              gap: 8px;
              padding: 8px;
              background: #f8f9fa;
              border-bottom: 1px solid #dee2e6;
              position: sticky;
              top: 0;
              z-index: 1;
            `}>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={rotateLeft}
              title="Rotate Left">
              <i className="fas fa-undo" />
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={rotateRight}
              title="Rotate Right">
              <i className="fas fa-redo" />
            </Button>
            {rotation !== 0 && (
              <span
                css={css`
                  display: flex;
                  align-items: center;
                  font-size: 12px;
                  color: #6c757d;
                  margin-left: 8px;
                `}>
                Rotated {rotation}°
              </span>
            )}
          </div>
          <Document
            loading={'Loading PDF ...'}
            file={docUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            externalLinkTarget="_blank">
            {Array.from({ length: numPages }, (v, i) => i + 1).map((page) => (
              <Page loading="" pageNumber={page} scale={1} rotate={rotation} key={page} />
            ))}
          </Document>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onClose}>
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default CreateOrderDialog;
